import { tinymce } from './tinymce';
import { BrowserHelper, OutputsHelper, IOutputValue, EOutputLookupContext } from './';
import { IDropdownOption } from 'office-ui-fabric-react';
import store from '../store/store';
import types from '../store/types';
import { EModalType, IOutput, EOutputScope } from '../helpers';

export class OutputReferenceHelper {

	private static saveOnBlur:boolean = true;
	private static lastBlurTimeStamp:number;
	private static postBlurModalScrollTop:number = null;
	private static autoFocusOnEditor: boolean = true;
	private static focusedEditorId: string;
	private static ignoreSetFocusedOutput: boolean = false;
	private static ignoreSetFocusedOutputId: number;

	static setFocusedEditorId = (editorId: string) => {
		OutputReferenceHelper.focusedEditorId = editorId;

		//remove focused-content-editable from all content-editable-wrappers
		let wrappers = document.querySelectorAll('.content-editable-wrapper');
		for(let i = 0; i < wrappers.length; i++) {
			let wrapper:HTMLElement = <HTMLElement>wrappers[i];
			//https://stackoverflow.com/questions/20306204/using-queryselector-with-ids-that-are-numbers will fix the error on the line below
			if(editorId && editorId.match(/^[0-9]/)) {
				editorId = "\\3" + editorId.charAt(0) + " " + editorId.substring(1);
			}
			if(!editorId || !wrapper.querySelector('#' + editorId)) {
				wrapper.classList.remove('focused-content-editable');
			}
		}

		//add focused-content-editable to the content-editable-wrapper that contains the editor
		if(editorId) {
			let editor = tinymce.get(editorId);
			if(editor) {
				let wrapper = OutputReferenceHelper.getEditorWrapper(editor);
				if(wrapper && !wrapper.classList.contains('focused-content-editable')) {
					wrapper.classList.add('focused-content-editable');
				}
			}
		}
	}

	static getFocusedEditorId = () => {
		return OutputReferenceHelper.focusedEditorId;
	}

	static getModal = () => {

		return document.querySelector('.ms-Dialog-main');

	};

	static setPostBlurModalScrollTop = (value: number) => {

		OutputReferenceHelper.postBlurModalScrollTop = value;

	}

	static getPostBlurModalScrollTop = () => {

		return OutputReferenceHelper.postBlurModalScrollTop;

	}

	/**
	 * In order to get tabindex working with the ContentEditableTinyMCE component it is necessary to have the tabindex set on the wrapper,
	 * which will then auto-focus on the editor when it comes into focus
	 * However, when tabbing out of the ContentEditableTinyMCE component, we need to move back out to the wrapper before going to the next item in the tabindex.
	 * So, when we move back out to the wrapper, the autoFocusOnEditor property is used to tell the auto-focus code to not go and focus on the editor as we have
	 * just come from there
	 */
	static setAutoFocusOnEditor = (newValue:boolean):void => {

		OutputReferenceHelper.autoFocusOnEditor = newValue;

	}

	static getAutoFocusOnEditor = ():boolean => {

		return OutputReferenceHelper.autoFocusOnEditor;

	}

	static outputReferenceSearch = (searchTerm: string, searchElement: HTMLElement, editor, outputLookupContext: EOutputLookupContext) => {

		let isSingleLine = editor.getBody().className.indexOf('single-line') >= 0;
		let offsetTop:number = 0;

		if(isSingleLine) {
			let editorElement = editor.contentWindow.frameElement ? <HTMLElement>editor.contentWindow.frameElement : editor.getBody();
			let editorPosition = OutputReferenceHelper.getElementPositionInComponent(editorElement);
			let editorHeight = editorElement.clientHeight;

			offsetTop = editorPosition.top + editorHeight;
		}
		else {
			let searchElementPosition = OutputReferenceHelper.getElementPositionInComponent(searchElement);
			let editorPosition = editor.contentWindow.frameElement ? OutputReferenceHelper.getElementPositionInComponent(<HTMLElement>editor.contentWindow.frameElement) : { top : 0 };
			let scrollOffsetTop = editor.contentWindow.frameElement ? 
				searchElement.ownerDocument.documentElement.scrollTop !== 0 || 
				(!searchElement.ownerDocument.scrollingElement || searchElement.ownerDocument.scrollingElement.scrollTop === 0) ?
				searchElement.ownerDocument.documentElement.scrollTop // IE & Chrome
				:
				searchElement.ownerDocument.scrollingElement.scrollTop // Microsoft Edge
			: 0;
			let elementHeight = searchElement.clientHeight;

			offsetTop = searchElementPosition.top + editorPosition.top + elementHeight + 5 - scrollOffsetTop ;
		}

		if(searchTerm.length && searchTerm[0] === '#') {
			searchTerm = searchTerm.substring(1);
		}
		
		store.dispatch({
			type: types.SET_OUTPUT_SEARCH,
			searchElementId: searchElement.id,
			searchTerm: searchTerm,
			offsetTop: offsetTop,
			outputLookupContext: outputLookupContext || EOutputLookupContext.NotSpecified
		});

	}

	static clearOutputReferenceSearch = () => {

		store.dispatch({
			type: types.CLEAR_OUTPUT_SEARCH
		});

	}

	static getSuppressNodeChange = () => {
		return (OutputReferenceHelper.lastBlurTimeStamp + 250) > OutputReferenceHelper.getTimeStamp();
	}

	static setLastBlurTimeStamp = () => {
		OutputReferenceHelper.lastBlurTimeStamp = OutputReferenceHelper.getTimeStamp();
	}

	static getTimeStamp = ():number => {
		return Math.floor(Date.now());
	}

	static moveToAfterNode = (node) => {
		let editor = OutputReferenceHelper.getParentEditor(node);
		if(editor) {
			editor.focus();
		}
		node.parentElement.focus();
		OutputReferenceHelper.placeCaretAfterNode(node);		
	}

	static moveToBeforeNode = (node) => {
		let editor = OutputReferenceHelper.getParentEditor(node);
		if(editor) {
			editor.focus();
		}
		node.parentElement.focus();
		OutputReferenceHelper.placeCaretBeforeNode(node);				
	}

	static getParentEditor = (node) => {
		let editorId = node.ownerDocument.body.getAttribute('data-id');
		if(editorId) {
			let editor = tinymce.get(editorId);
			if(editor) {
				return editor;
			}
		}
		return null;
	}

	static getFixedOutputs = (editor) => {
		return editor.contentDocument.querySelectorAll('.output-token.fixed');
	}

	static outputSelect = (key: string, searchElementId: string, arrayOutputIndexKey?: string) => {

		let noIndex:boolean = arrayOutputIndexKey =="00000000-0000-0000-0000-000000000000";
		
		let output: IOutput = OutputsHelper.GetOutput(key);
		if(output.IsMultiple && arrayOutputIndexKey == null && !noIndex) {
			
			store.dispatch({
				type: types.SET_INDEX_REQUEST_OUTPUT_ID,
				outputLookupRequestIndexForOutputId: output.Id
			});

		}
		else {

			let searchNode = tinymce.activeEditor.contentDocument.getElementById(searchElementId);
			let parent = searchNode.parentElement;

			let hasWrapper = OutputReferenceHelper.isOutputWrapper(parent); //this is a fix required for IE

			let nodeToFix = hasWrapper ? parent : searchNode;

			if(hasWrapper) {
				OutputReferenceHelper.moveToAfterNode(nodeToFix);
			}

			
			if(arrayOutputIndexKey && !noIndex) {
				let indexOutput = OutputsHelper.GetOutput(arrayOutputIndexKey)
				OutputReferenceHelper.fixOutputElement(nodeToFix, key + '[' + arrayOutputIndexKey + ']', output.Name + '[' + indexOutput.Name + ']', true);
			}
			else {
				OutputReferenceHelper.fixOutputElement(nodeToFix, key, output.Name);
			}

			

			if(!hasWrapper) {
				OutputReferenceHelper.moveToAfterNode(nodeToFix);
			}

			OutputReferenceHelper.setSaveOnBlur(true);

		}
	}

	static decodeTokens = (value: string, outputOptions: IDropdownOption[]) => {

		let outputTokenRegEx = new RegExp('##' + '[0-9,a-f,-]\*' + '##', 'gi');		
		let outputIdentifierRegEx = new RegExp('##', 'g');

		value = value || '';

		let indexedOutputTokens: string[] = value.match(/##+.{36}(\[.*]{1})##/gi)

		for(let i = 0; i < (indexedOutputTokens ? indexedOutputTokens.length : 0); i++) {

			let token: string = indexedOutputTokens[i];
			let outputs: string[] = token.match(/[0-9,a-f,-]{36}/gi);
			if(outputs.length == 2) {

				let arrayOutputId = outputs[0];
				let indexOutputId = outputs[1];

				let arrayOutput = outputOptions.filter(o => o.key === arrayOutputId)[0];
				let indexOutput = outputOptions.filter(o => o.key === indexOutputId)[0];

				if(arrayOutput && indexOutput) {

					let outputTokenElement = document.createElement('span');
					OutputReferenceHelper.fixOutputElement(outputTokenElement, arrayOutput.key + '[' + indexOutput.key + ']', 
						arrayOutput.text + '[' + indexOutput.text + ']', true);
					
					value = value.replace(indexedOutputTokens[i], outputTokenElement.outerHTML);
				}

			}

		}

		let outputTokens = value.match(outputTokenRegEx);

		for (let i = 0; i < (outputTokens ? outputTokens.length : 0); i++) {

			let outputId = outputTokens[i].replace(outputIdentifierRegEx, '');
			let outputOption = outputOptions.filter(o => o.key === outputId)[0];
			if (outputOption) {

				let outputTokenElement = document.createElement('span');
				OutputReferenceHelper.fixOutputElement(outputTokenElement, outputOption.key.toString(), outputOption.text);
				value = value.replace(outputTokens[i], outputTokenElement.outerHTML);

			}

		}

		return value;

	}

	static fixOutputElement = (nodeToFix: HTMLElement, key: string, text: string, isArrayOutput?: boolean) => {

		nodeToFix.className = 'output-token fixed'  + (isArrayOutput ? ' array-output-token' : '');
		let tokenText = text;
		if(tokenText.length >= 15) {
			tokenText = tokenText.substring(0, 12) + '...';
		}
		nodeToFix.innerHTML = tokenText;
		nodeToFix.setAttribute('title', text);
		nodeToFix.removeAttribute('id');
		if(BrowserHelper.detectIE()) {
			nodeToFix.removeAttribute('contenteditable');
		}
		else {
			nodeToFix.setAttribute('contenteditable', 'false');
		}
		nodeToFix.setAttribute('output-id', key);
		OutputReferenceHelper.registerDragPrevention(nodeToFix);

		let deleteButtonElement = document.createElement('span');
		deleteButtonElement.setAttribute('class', 'output-token-delete ms-Icon ms-Icon--Cancel');

		OutputReferenceHelper.registerDeleteHandler(deleteButtonElement);

		let deleteButtonWrapper = document.createElement('span');
		deleteButtonWrapper.setAttribute('class', 'output-token-delete-wrapper');
		deleteButtonWrapper.setAttribute('contenteditable', 'false');
		deleteButtonWrapper.appendChild(deleteButtonElement);	
		nodeToFix.appendChild(deleteButtonWrapper);

	}

	static createTextNodeAfter = (editor, elem) => {

		let content = '\u200B';

		if(elem.nextSibling) {

			if(elem.nextSibling.tagName && elem.nextSibling.tagName.toLowerCase() === 'br') { //firefox fix, as firefox puts br elements in, which get in the way
				elem.parentElement.removeChild(elem.nextSibling);
			}
			else if(elem.nextSibling.textContent.length < 1) {
				elem.nextSibling.textContent = content + elem.nextSibling.textContent;
			}
			else if(elem.nextSibling.textContent.charCodeAt(0) !== 8203) { 
				elem.nextSibling.textContent = content + elem.nextSibling.textContent;
			}		

		}
		else if(!elem.nextSibling) {
			var textNode = document.createTextNode(content);
			editor.dom.insertAfter(textNode, elem);
		}		
	};

	static registerDragPrevention = (element: HTMLElement) => {

		element.onmousedown = (e) => {
			e.preventDefault();
			e.stopImmediatePropagation();
			e.stopPropagation();
			return true;
		};

	}

	private static mouseDownElement:HTMLElement;
	static registerDeleteHandler = (deleteButtonElement: HTMLElement) => {

		deleteButtonElement.onmousedown = (e) => { 
			OutputReferenceHelper.mouseDownElement = deleteButtonElement;
		};

		deleteButtonElement.onmouseup = (e) => { 
			
			if(OutputReferenceHelper.mouseDownElement === deleteButtonElement) {
				let token = deleteButtonElement.parentElement.parentElement;
				OutputReferenceHelper.moveToBeforeNode(token);
				token.parentElement.removeChild(token);
			}
			
		};

	}

	static setSaveOnBlur = (newValue: boolean) => {
		OutputReferenceHelper.saveOnBlur = newValue;
	}

	static getSaveOnBlur = () => {
		return OutputReferenceHelper.saveOnBlur;
	}
	static setFocusedOutputKey = (focusedOutputKey: string) => {
		if(!OutputReferenceHelper.ignoreSetFocusedOutput) {
			store.dispatch({
				type: types.SET_FOCUSED_OUTPUT_KEY,
				focusedOutputKey: focusedOutputKey
			});
		}	
	}

	static moveFocusedOutputKey = (next: boolean) => {

		let state = store.getState();
		let allOutputs: IOutputValue[] = OutputsHelper.combineOutputs(state.outputsState.outputs, state.outputsState.globalOutputs, state.modalState.modal.parentLoopId);
		let currentComponentId: string = null;

		if(!allOutputs || !allOutputs.length) {
			return;
		}

		if(state.modalState) {
			if(state.modalState.openModal === EModalType.SharedJourneyConfig) {
				currentComponentId = state.modalState.sharedJourneyConfig.SharedJourney
				.ComponentId;
			}
		}

		if(currentComponentId){
			allOutputs = allOutputs.filter(o => o.componentId !== currentComponentId);
		}
		
		let previousOutputBlocks = state.outputsState.previousOutputBlocks;
		let outputSearchTerm = state.outputsState.outputSearchTerm;
		let focusedOutputKey = state.outputsState.focusedOutputKey;

		//get the available outputs
		let outputOptions: IDropdownOption[] = OutputsHelper.getFilteredOutputDropdownOptions(allOutputs, previousOutputBlocks, outputSearchTerm, currentComponentId);

		//get the current output
		let currentOutputIndex: number = null;
		for(let i = 0; i < outputOptions.length; i++) {
			if(outputOptions[i].key === focusedOutputKey) {
				currentOutputIndex = i;
				break;
			}
		}

		//go to the next one in the list (cycle round to the beginning if necessary)
		let newOutputIndex: number = 0;
	
		if(next) {
			newOutputIndex = currentOutputIndex === null ? 0: (currentOutputIndex + 1) % outputOptions.length;
		}
		else {
			newOutputIndex = currentOutputIndex === null ? outputOptions.length - 1 : (currentOutputIndex - 1);
			newOutputIndex = newOutputIndex < 0 ? outputOptions.length - 1 : newOutputIndex;
		}

		store.dispatch({
			type: types.SET_FOCUSED_OUTPUT_KEY,
			focusedOutputKey: outputOptions[newOutputIndex].key
		});
	
		//if any mouse over events happen in the interim, then this flag will ensure that there effects on the focused output are ignored
		if(OutputReferenceHelper.ignoreSetFocusedOutputId) {
			clearTimeout(OutputReferenceHelper.ignoreSetFocusedOutputId);
		}
		OutputReferenceHelper.ignoreSetFocusedOutput = true;
		OutputReferenceHelper.ignoreSetFocusedOutputId = setTimeout(() => {
			OutputReferenceHelper.ignoreSetFocusedOutput = false;
		}, 500);

		//scroll to the newly selected one
		let newSelectionElem = document.getElementById('output_' + outputOptions[newOutputIndex].key);
		let outputSelect = document.querySelector('ul.suggestions.output-select');
		let scrollTop = outputSelect.scrollTop;
		let scrollBottom = scrollTop + outputSelect.clientHeight;
		let newSelectionElementTop = newSelectionElem.offsetTop;
		let newSelectionElementBottom = newSelectionElem.offsetTop + newSelectionElem.clientHeight;
		let newElementIsVisible = 
			newSelectionElementTop >= scrollTop && 
			newSelectionElementBottom <= scrollBottom;
		
		if(!newElementIsVisible) {
			if(newOutputIndex === 0) {
				outputSelect.scrollTop = 0;
			}
			else if(newOutputIndex === outputOptions.length - 1) {
				outputSelect.scrollTop = newSelectionElem.offsetTop;
			}
			else if(newOutputIndex > currentOutputIndex) {
				outputSelect.scrollTop = outputSelect.scrollTop + newSelectionElem.clientHeight;
			}
			else if(newOutputIndex < currentOutputIndex) {
				outputSelect.scrollTop = outputSelect.scrollTop - newSelectionElem.clientHeight;
			}
		}

	}

	static nextFocusedOutputKey = () => {
		OutputReferenceHelper.moveFocusedOutputKey(true);
	}

	static previousFocusedOutputKey = () => {
		OutputReferenceHelper.moveFocusedOutputKey(false);
	}

	static selectFocusedOutputKey = () => {
		let focusedOutputKey = store.getState().outputsState.focusedOutputKey;
		let state = store.getState();
		let allOutputs: IOutputValue[] = OutputsHelper.combineOutputs(state.outputsState.outputs, state.outputsState.globalOutputs, state.modalState.modal.parentLoopId);
		let currentComponentId: string = null;

		if(!allOutputs || !allOutputs.length) {
			return;
		}

		if(state.modalState) {

			if(state.modalState.openModal === EModalType.ControlConfig) {
				currentComponentId = state.modalState.controlConfig.componentId;
			}

			else if(state.modalState.openModal === EModalType.ActionConfig) {
				currentComponentId = state.modalState.actionConfig.componentId;
			}

			else if(state.modalState.openModal === EModalType.SharedJourneyConfig) {
				currentComponentId = state.modalState.sharedJourneyConfig.SharedJourney
				.ComponentId;
			}

		}

		if(currentComponentId){
			allOutputs = allOutputs.filter(o => o.componentId !== currentComponentId);
		}

		let previousOutputBlocks = state.outputsState.previousOutputBlocks;
		let outputOptions: IDropdownOption[] = OutputsHelper.getOutputDropdownOptions(allOutputs, previousOutputBlocks);
		let outputSearchElementId: string = state.outputsState.outputSearchElementId;
		let focusedOutput: IDropdownOption = outputOptions.filter(o => o.key === focusedOutputKey)[0];
		if(focusedOutput.text) {
			OutputReferenceHelper.outputSelect(focusedOutputKey, outputSearchElementId);
		}
	}

	static isOutputReference = (node):boolean => {
		
		if(!node.className) {
			return false;
		}
		
		return node.className.indexOf('output-token') >= 0 && node.className.indexOf('fixed') < 0;
	};

	static isFixedOutputReference = (node):boolean => {

		if(!node.className) {
			return false;
		}

		return (node.className.indexOf('output-token') >= 0 && node.className.indexOf('fixed') >= 0) || node.className.indexOf('output-token-delete') >= 0 || node.className.indexOf('output-token-delete-wrapper') >= 0;

	};

	static isOutputWrapper = (node):boolean => {
		
		if(!node.className) {
			return false;
		}
		
		return node.className.indexOf('output-wrapper') >= 0;
	};

	static placeCaretAfterNode = (node) => {

		OutputReferenceHelper.createTextNodeAfter(tinymce.activeEditor, node);
		if (typeof tinymce.activeEditor.contentWindow.getSelection !== "undefined") {
			var range = tinymce.activeEditor.contentDocument.createRange();
			range.setStartAfter(node);
			range.collapse(true);
			var selection = tinymce.activeEditor.contentWindow.getSelection();
			if(selection) {
				selection.removeAllRanges();
				selection.addRange(range);
			}
		}

	};

	static placeCaretBeforeNode = (node) => {

		if (typeof tinymce.activeEditor.contentWindow.getSelection !== "undefined") {
			var range = tinymce.activeEditor.contentDocument.createRange();
			range.setStartBefore(node);
			range.collapse(true);
			var selection = tinymce.activeEditor.contentWindow.getSelection();
			if(selection) {
				selection.removeAllRanges();
				selection.addRange(range);
			}
		}

	}

	static getElementPositionInComponent = (elem: HTMLElement) => {

		let boundingClientRect = elem.getBoundingClientRect();
		let offsetTop: number = 0, offsetLeft: number = 0;

		while(elem && elem.className.indexOf('cascade') < 0) {
			offsetTop += elem.offsetTop;
			offsetLeft += elem.offsetLeft;
			elem = <HTMLElement>elem.offsetParent;
		}		

		return {
			top: offsetTop,
			left: offsetLeft,
			bottom: offsetTop + boundingClientRect.height,
			right: offsetLeft + boundingClientRect.width
		};
	}

	static setTabIndices = (editor) => {
		let elements = document.querySelectorAll('.mce-container');
		for(let i = 0; i < elements.length; i++) {
			elements[i].removeAttribute('tabindex');
		}

		//set tab index of body to -1, so that it can only be focused on programmatically
		editor.contentDocument.body.setAttribute('tabindex', '-1');
		
	}

	static getEditorWrapper = (editor) => {
		let frame = editor.contentWindow.frameElement;
		let parent = frame.parentElement;

		while(parent.className.indexOf('content-editable-wrapper') < 0) {
			parent = parent.parentElement;
			if(parent.tagName.toLowerCase() === 'body') { //somehow missed the wrapper
				return null;
			}
		}	

		return parent;
	}

	static moveFocusToWrapper = (editor) => {

		let wrapper = OutputReferenceHelper.getEditorWrapper(editor);
		if(wrapper) {
			wrapper.focus(); //found the wrapper, so focus on it
		}

	}

	static getTinyMCEEditorBody = (wrapperId: string) => {
		let wrapper = document.getElementById(wrapperId);
		let frame = wrapper.querySelector('iframe');
		return frame ? frame.contentDocument.body : null;
	}

	static getTinyMCEEditor = (wrapperId: string) => {
		let editorBody = OutputReferenceHelper.getTinyMCEEditorBody(wrapperId);
		if(editorBody) {
			let editorId = editorBody.getAttribute('data-id');
			return tinymce.get(editorId);
		}
		else {
			return null;
		}
	}

	static focusOnTinyMCEEditor = (wrapperId: string) => {

		let editorBody = OutputReferenceHelper.getTinyMCEEditorBody(wrapperId);
		(<HTMLElement>editorBody.firstElementChild).focus();
		editorBody.focus();

	}

	static getTinyMCEEditorValue = (wrapperId: string) => {
		let editor = OutputReferenceHelper.getTinyMCEEditor(wrapperId);
		return editor ? editor.getContent() : null;
	}

	static setTinyMCEEditorContent = (wrapperId: string, newContent: string) => {
		let editor = OutputReferenceHelper.getTinyMCEEditor(wrapperId);
		if(editor) {
			editor.setContent(newContent);
		}
	}

	static initialiseTinyMCEEditorFromWrapperId = (wrapperId) => {
		let editor = OutputReferenceHelper.getTinyMCEEditor(wrapperId);
		if(editor) {
			OutputReferenceHelper.registerEventHandlers(editor);
		}
	}

	static registerEventHandlers = (editor) => {

		let deleteButtons = editor.contentDocument.querySelectorAll('.output-token-delete');
		for(let i = 0; i < deleteButtons.length; i++) {
			OutputReferenceHelper.registerDragPrevention(deleteButtons[i].parentElement);
			OutputReferenceHelper.registerDeleteHandler(deleteButtons[i]);
		}

		editor.contentDocument.body.addEventListener('blur', (e) => {

			if(!BrowserHelper.detectIE()) {

				let modal = OutputReferenceHelper.getModal();
				OutputReferenceHelper.setPostBlurModalScrollTop(modal ? modal.scrollTop : 0);	

			}

		});

		OutputReferenceHelper.setTabIndices(editor);
	}

	static encodeTokens = (value: string) => {
		let encoded = document.createElement('div');
		encoded.innerHTML = value;

		let tokens = encoded.querySelectorAll('.output-token');

		for(let i = 0; i < tokens.length; i++) {
			let token:any = tokens[i];
			let outputId = token.getAttribute('output-id');
			token.outerHTML = outputId ? '##' + outputId + '##' : '';
		}

		return encoded.innerHTML;
	}

	static outputArraysAreEqual = (a: IDropdownOption[], b: IDropdownOption[]) => {
		if(a.length !== b.length) {
			return false;
		}

		for(let i = 0; i < a.length; i++) {
			if(a[i].key !== b[i].key) {
				return false;
			}
		}

		return true;
	}

}


// WEBPACK FOOTER //
// ./source/ts/helpers/output-reference.ts